import React, { useState, useEffect } from 'react'
import { 
  Save,
  RotateCcw,
  UserPlus,
  Calendar,
  Target,
  Building,
  DollarSign,
  Plus,
  ChevronRight,
  Home,
  Users,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import { leadGenerationService } from '../../services/leadGenerationService'
import prospectMasterService from '../../services/prospectMasterService'
import leadSourceMasterService from '../../services/leadSourceMasterService'
import { campaignMasterService } from '../../services/campaignMasterService'

const LeadManagementForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('leadInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingLeadCode, setIsLoadingLeadCode] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    // Lead Information
    leadCode: '',
    leadDate: new Date().toISOString().split('T')[0],
    leadCategory: '',
    leadStatus: 'New',
    priority: 'Medium',
    
    // Links to Masters
    prospect: '',
    leadSource: '',
    campaign: '',
    
    // Business Details
    msmeCategory: '',
    industrySegment: '',
    interestArea: '',
    interestLevel: '',
    
    // Technical Details
    existingSoftware: '',
    requestedDemoDate: '',
    demoType: '',
    leadOwner: '',
    
    // Qualification Details
    budget: 0,
    timeline: '',
    decisionMaker: '',
    painPoints: '',
    requirements: '',
    qualificationScore: 0,
    
    // Follow-up Information
    nextFollowUpDate: '',
    followUpNotes: '',
    notes: ''
  })

  // Master data for dropdowns
  const [prospects, setProspects] = useState([])
  const [leadSources, setLeadSources] = useState([])
  const [campaigns, setCampaigns] = useState([])

  // Load master data and lead code on component mount
  useEffect(() => {
    loadLeadCode()
    loadMasterData()
  }, [])

  const loadLeadCode = async () => {
    try {
      setIsLoadingLeadCode(true)
      const response = await autoIncrementService.getCurrentValue('LEAD-MGMT')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          leadCode: sequence
        }))
      }
    } catch (error) {
      console.error('Error loading lead code:', error)
      toast.error('Failed to load lead code')
    } finally {
      setIsLoadingLeadCode(false)
    }
  }

  const loadMasterData = async () => {
    try {
      // Load prospects
      const prospectsResponse = await prospectMasterService.getAllProspects({ limit: 1000 })
      if (prospectsResponse.success) {
        setProspects(prospectsResponse.data.prospects)
      }

      // Load lead sources
      const leadSourcesResponse = await leadSourceMasterService.getAllLeadSources({ limit: 1000 })
      if (leadSourcesResponse.success) {
        setLeadSources(leadSourcesResponse.data.leadSources)
      }

      // Load campaigns
      const campaignsResponse = await campaignMasterService.getAllCampaigns({ limit: 1000 })
      if (campaignsResponse.success) {
        setCampaigns(campaignsResponse.data.campaigns)
      }
    } catch (error) {
      console.error('Error loading master data:', error)
      toast.error('Failed to load master data')
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      
      // Get next sequence number for lead code
      const sequenceResponse = await autoIncrementService.getNextValue('LEAD-MGMT')
      if (!sequenceResponse.success) {
        toast.error('Failed to generate lead code')
        return
      }
      
      const leadData = {
        ...formData,
        leadCode: sequenceResponse.data.formattedValue
      }
      
      const response = await leadGenerationService.createLead(leadData)
      
      if (response.success) {
        toast.success('Lead created successfully')
        navigate('/crm/lead-management')
      } else {
        toast.error(response.message || 'Failed to create lead')
      }
    } catch (error) {
      console.error('Error creating lead:', error)
      toast.error('Failed to create lead')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      leadCode: '',
      leadDate: new Date().toISOString().split('T')[0],
      leadCategory: '',
      leadStatus: 'New',
      priority: 'Medium',
      prospect: '',
      leadSource: '',
      campaign: '',
      msmeCategory: '',
      industrySegment: '',
      interestArea: '',
      interestLevel: '',
      existingSoftware: '',
      requestedDemoDate: '',
      demoType: '',
      leadOwner: '',
      budget: 0,
      timeline: '',
      decisionMaker: '',
      painPoints: '',
      requirements: '',
      qualificationScore: 0,
      nextFollowUpDate: '',
      followUpNotes: '',
      notes: ''
    })
    loadLeadCode()
  }

  const helpContent = (
    <>
      <h3>What is Lead Management?</h3>
      <p>Lead Management lets you <strong>create and manage new leads</strong>—potential opportunities linked to prospects, lead source, and campaigns. You can capture category, status, priority, interest, qualification details, and follow-up information.</p>
      <h3>Tabs</h3>
      <p><strong>Lead Info</strong> – Lead code, date, category, status, priority, prospect, lead source, campaign, and business/technical details (e.g. demo type, owner).</p>
      <p><strong>Qualification</strong> – Budget, timeline, decision maker, pain points, requirements, and qualification score.</p>
      <p><strong>Follow-up</strong> – Next follow-up date and notes.</p>
      <h3>How to use</h3>
      <p>Fill required fields, link to a prospect/lead source/campaign if needed. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Lead Management', path: '/crm/lead-management' },
          { label: 'Create', isCurrent: true }
        ]}
        rightSlot={
          <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
            <HelpCircle size={18} />
            <span className="d-none d-sm-inline">Help</span>
          </button>
        }
      />
      
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>Lead Management</h1>
            <p>Create and manage new lead</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Lead ( Create )</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'leadInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('leadInfo')}
          >
            <UserPlus size={18} />
            <span>Lead Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'business' ? 'active' : ''}`}
            onClick={() => setActiveTab('business')}
          >
            <Building size={18} />
            <span>Business Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'qualification' ? 'active' : ''}`}
            onClick={() => setActiveTab('qualification')}
          >
            <Target size={18} />
            <span>Qualification</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'followup' ? 'active' : ''}`}
            onClick={() => setActiveTab('followup')}
          >
            <Calendar size={18} />
            <span>Follow-up</span>
          </button>
        </div>
          
        <form className="form-content" onSubmit={handleSubmit}>

          {/* Lead Info Tab */}
          {activeTab === 'leadInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Lead Information</h3>
              </div>
              
              {/* Row 1: Lead Code, Lead Date, Lead Category, Priority */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="leadCode" className="form-label required">
                    Lead Code
                  </label>
                  <input
                    type="text"
                    id="leadCode"
                    name="leadCode"
                    value={isLoadingLeadCode ? 'Generating...' : formData.leadCode}
                    readOnly
                    className="form-input"
                    placeholder="Enter lead code"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadDate" className="form-label required">
                    Lead Date
                  </label>
                  <input
                    type="date"
                    id="leadDate"
                    name="leadDate"
                    value={formData.leadDate}
                    onChange={(e) => handleInputChange('leadDate', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadCategory" className="form-label required">
                    Lead Category
                  </label>
                  <select
                    id="leadCategory"
                    name="leadCategory"
                    value={formData.leadCategory}
                    onChange={(e) => handleInputChange('leadCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    <option value="New">New</option>
                    <option value="Existing">Existing</option>
                    <option value="Referral">Referral</option>
                    <option value="Cold Call">Cold Call</option>
                    <option value="Website">Website</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Event">Event</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="priority" className="form-label required">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="form-input"
                  >
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Master Selections */}
              <div className="section-header">
                <h3>Master Selections</h3>
              </div>
              
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="prospect" className="form-label required">
                    Select Prospect
                  </label>
                  <select
                    id="prospect"
                    name="prospect"
                    value={formData.prospect}
                    onChange={(e) => handleInputChange('prospect', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Prospect</option>
                    {prospects.map(prospect => (
                      <option key={prospect._id} value={prospect._id}>
                        {prospect.prospectName} - {prospect.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="leadSource" className="form-label required">
                    Select Lead Source
                  </label>
                  <select
                    id="leadSource"
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={(e) => handleInputChange('leadSource', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Lead Source</option>
                    {leadSources.map(source => (
                      <option key={source._id} value={source._id}>
                        {source.leadSourceName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="campaign" className="form-label">
                    Select Campaign
                  </label>
                  <select
                    id="campaign"
                    name="campaign"
                    value={formData.campaign}
                    onChange={(e) => handleInputChange('campaign', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Campaign (Optional)</option>
                    {campaigns.map(campaign => (
                      <option key={campaign._id} value={campaign._id}>
                        {campaign.campaignName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Lead Status */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="leadStatus" className="form-label required">
                    Lead Status
                  </label>
                  <select
                    id="leadStatus"
                    name="leadStatus"
                    value={formData.leadStatus}
                    onChange={(e) => handleInputChange('leadStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="New">New</option>
                    <option value="Qualified">Qualified</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Demo Scheduled">Demo Scheduled</option>
                    <option value="Proposal Sent">Proposal Sent</option>
                    <option value="Negotiation">Negotiation</option>
                    <option value="Converted">Converted</option>
                    <option value="Lost">Lost</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Business Details Tab */}
          {activeTab === 'business' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Business Details</h3>
              </div>
              
              {/* Row 1: MSME Category, Industry/Segment, Interest Area, Interest Level */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="msmeCategory" className="form-label">
                    MSME Category
                  </label>
                  <select
                    id="msmeCategory"
                    name="msmeCategory"
                    value={formData.msmeCategory}
                    onChange={(e) => handleInputChange('msmeCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select MSME Category</option>
                    <option value="Micro">Micro</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Enterprise">Enterprise</option>
                    <option value="Startup">Startup</option>
                    <option value="SME">SME</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="industrySegment" className="form-label required">
                    Industry/Segment
                  </label>
                  <input
                    type="text"
                    id="industrySegment"
                    name="industrySegment"
                    value={formData.industrySegment}
                    onChange={(e) => handleInputChange('industrySegment', e.target.value)}
                    className="form-input"
                    placeholder="Enter industry/segment"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interestArea" className="form-label required">
                    Interest Area/Primary Focus
                  </label>
                  <select
                    id="interestArea"
                    name="interestArea"
                    value={formData.interestArea}
                    onChange={(e) => handleInputChange('interestArea', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Interest Area</option>
                    <option value="ERP">ERP</option>
                    <option value="CRM">CRM</option>
                    <option value="HRMS">HRMS</option>
                    <option value="Accounting">Accounting</option>
                    <option value="Inventory">Inventory</option>
                    <option value="Project Management">Project Management</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="interestLevel" className="form-label required">
                    Interest Level
                  </label>
                  <select
                    id="interestLevel"
                    name="interestLevel"
                    value={formData.interestLevel}
                    onChange={(e) => handleInputChange('interestLevel', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Interest Level</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Existing Software, Requested Demo Date, Demo Type, Lead Owner */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="existingSoftware" className="form-label">
                    Existing Software
                  </label>
                  <input
                    type="text"
                    id="existingSoftware"
                    name="existingSoftware"
                    value={formData.existingSoftware}
                    onChange={(e) => handleInputChange('existingSoftware', e.target.value)}
                    className="form-input"
                    placeholder="Enter existing software"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="requestedDemoDate" className="form-label">
                    Requested Demo Date (RDD)
                  </label>
                  <input
                    type="date"
                    id="requestedDemoDate"
                    name="requestedDemoDate"
                    value={formData.requestedDemoDate}
                    onChange={(e) => handleInputChange('requestedDemoDate', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demoType" className="form-label">
                    Demo Type
                  </label>
                  <select
                    id="demoType"
                    name="demoType"
                    value={formData.demoType}
                    onChange={(e) => handleInputChange('demoType', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Demo Type</option>
                    <option value="Online">Online</option>
                    <option value="In-Person">In-Person</option>
                    <option value="Recorded">Recorded</option>
                    <option value="Live Demo">Live Demo</option>
                    <option value="POC">POC</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="leadOwner" className="form-label">
                    Lead Owner
                  </label>
                  <input
                    type="text"
                    id="leadOwner"
                    name="leadOwner"
                    value={formData.leadOwner}
                    onChange={(e) => handleInputChange('leadOwner', e.target.value)}
                    className="form-input"
                    placeholder="Enter lead owner"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Qualification Tab */}
          {activeTab === 'qualification' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Qualification Details</h3>
              </div>
              
              {/* Row 1: Budget, Timeline, Decision Maker, Qualification Score */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="budget" className="form-label">
                    Budget
                  </label>
                  <input
                    type="number"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="Enter budget"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="timeline" className="form-label">
                    Timeline
                  </label>
                  <input
                    type="text"
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="form-input"
                    placeholder="Enter timeline"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="decisionMaker" className="form-label">
                    Decision Maker
                  </label>
                  <input
                    type="text"
                    id="decisionMaker"
                    name="decisionMaker"
                    value={formData.decisionMaker}
                    onChange={(e) => handleInputChange('decisionMaker', e.target.value)}
                    className="form-input"
                    placeholder="Enter decision maker"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="qualificationScore" className="form-label">
                    Qualification Score (0-100)
                  </label>
                  <input
                    type="number"
                    id="qualificationScore"
                    name="qualificationScore"
                    value={formData.qualificationScore}
                    onChange={(e) => handleInputChange('qualificationScore', parseInt(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0-100"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              {/* Row 2: Pain Points, Requirements */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="painPoints" className="form-label">
                    Pain Points
                  </label>
                  <textarea
                    id="painPoints"
                    name="painPoints"
                    value={formData.painPoints}
                    onChange={(e) => handleInputChange('painPoints', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter pain points"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="requirements" className="form-label">
                    Requirements
                  </label>
                  <textarea
                    id="requirements"
                    name="requirements"
                    value={formData.requirements}
                    onChange={(e) => handleInputChange('requirements', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter requirements"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Follow-up Tab */}
          {activeTab === 'followup' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Follow-up Information</h3>
              </div>
              
              {/* Row 1: Next Follow-up Date */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="nextFollowUpDate" className="form-label">
                    Next Follow-up Date
                  </label>
                  <input
                    type="date"
                    id="nextFollowUpDate"
                    name="nextFollowUpDate"
                    value={formData.nextFollowUpDate}
                    onChange={(e) => handleInputChange('nextFollowUpDate', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 2: Follow-up Notes */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="followUpNotes" className="form-label">
                    Follow-up Notes
                  </label>
                  <textarea
                    id="followUpNotes"
                    name="followUpNotes"
                    value={formData.followUpNotes}
                    onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter follow-up notes"
                  />
                </div>
              </div>

              {/* Row 3: Additional Notes */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Additional Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter additional notes"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleReset}
              disabled={loading}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Lead Management – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default LeadManagementForm
